import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { isArray, isEmpty, join, lowerCase, map, omit, some, toNumber, trim } from 'lodash';
import React, { useState } from 'react';

import { PrepareCustomerResultsJobManifestTable } from 'components/Pages/Jobs/PrepareCustomerResultsJobManifestTable';
import CohortQueryList from 'components/SearchFilters/CohortQueryList';
import CohortQueryListItem from 'components/SearchFilters/CohortQueryListItem';
import { ResultsMode } from 'interfaces/experimentResults';
import { PrepareCustomerResultsResultType, prepareCustomerResultsResultTypeOptions } from 'interfaces/job';
import { humanize } from 'utils/helpers';
import { manualSelectionFilters } from 'utils/useCasesParams/constants';
import { CasesParams } from 'utils/useCasesParams/types';
import { useCurrentLabId } from 'utils/useCurrentLab';
import useStudy from 'utils/useStudy';
import { FilesToExportSelect } from './FilesToExportSelect';
import { usePrepareCustomerResultsMutation, usePreviewPrepareCustomerResultsExport } from './hooks';

interface Props {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  // Published study results export: fixed to every published result in the study, not the Dataset selection.
  wholeStudyPublishedOnly?: boolean;
  cancelLabel?: string;
}

const PrepareCustomerResultsJob: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  onClose,
  casesParams,
  wholeStudyPublishedOnly = false,
  cancelLabel = 'Cancel',
}) => {
  const studyId = casesParams?.filters?.studyId || casesParams?.studyId;
  const { data: study } = useStudy(studyId);
  const { labId } = useCurrentLabId();
  const prepareCustomerResultsMutation = usePrepareCustomerResultsMutation();

  const [filterByCaseParamsState, setFilterByCaseParams] = useState(false);

  const hasSpecificSelections = some(manualSelectionFilters, (filterName) => !isEmpty(casesParams?.[filterName]));

  const normalizedResultsMode = casesParams?.resultsMode || ResultsMode.Published;

  const caseParamsWithoutStudyId = {
    ...casesParams,
    resultsMode: normalizedResultsMode,
    filters: omit(casesParams?.filters, 'studyId'),
  };
  // Do we have filters beyond the default ones?
  const hasCaseParams =
    !wholeStudyPublishedOnly &&
    (hasSpecificSelections ||
      !isEmpty(
        omit(
          caseParamsWithoutStudyId,
          'filters',
          'labId',
          'slidesMode',
          'resultsMode',
          'clinicalData',
          'features',
          ...manualSelectionFilters
        )
      ) ||
      caseParamsWithoutStudyId.resultsMode !== ResultsMode.Published ||
      !isEmpty(caseParamsWithoutStudyId?.filters) ||
      !isEmpty(caseParamsWithoutStudyId?.clinicalData) ||
      !isEmpty(caseParamsWithoutStudyId?.features));

  const filterByCaseParams = filterByCaseParamsState && hasCaseParams;

  const [resultTypesToExport, setResultTypesToExport] = useState<PrepareCustomerResultsResultType[]>([
    ...prepareCustomerResultsResultTypeOptions,
  ]);
  const [zipGranularity, setZipGranularity] = useState<'slide' | 'case'>('case');
  const [generateFeaturesCsvFile, setGenerateFeaturesCsvFile] = useState<boolean>(false);
  const [publishResultsToCustomer, setPublishResultsToCustomer] = useState<boolean>(false);
  const [unitsPerZipInput, setUnitsPerZipInput] = useState<string>('10');
  const [shardLimitInput, setShardLimitInput] = useState<string>('');

  const handleFilesToExportChange = (event: SelectChangeEvent<string[]>) => {
    if (!isArray(event.target.value)) {
      setResultTypesToExport([event.target.value as PrepareCustomerResultsResultType]);
      return;
    }
    setResultTypesToExport(event.target.value as PrepareCustomerResultsResultType[]);
  };

  const parsedUnitsPerZip = toNumber(unitsPerZipInput);
  const hasValidUnitsPerZip = Number.isInteger(parsedUnitsPerZip) && parsedUnitsPerZip >= 1;
  const normalizedShardLimitInput = trim(shardLimitInput);
  const parsedShardLimit = isEmpty(normalizedShardLimitInput) ? undefined : toNumber(normalizedShardLimitInput);
  const hasValidShardLimit =
    isEmpty(normalizedShardLimitInput) || (Number.isInteger(parsedShardLimit) && parsedShardLimit >= 1);

  const allPublishedResultsQueryObject = {
    studyId,
    resultsMode: ResultsMode.Published,
  };

  const selectedScopeQueryObject = {
    ...caseParamsWithoutStudyId,
    studyId,
    filters: { ...caseParamsWithoutStudyId?.filters, studyId },
  };

  const queryObject = filterByCaseParams ? selectedScopeQueryObject : allPublishedResultsQueryObject;

  const mutationParams = {
    studyId,
    resultTypesToExport,
    zipGranularity,
    generateFeaturesCsvFile,
    publishResultsToCustomer,
    unitsPerZip: hasValidUnitsPerZip ? parsedUnitsPerZip : undefined,
    shardLimit: hasValidShardLimit ? parsedShardLimit : undefined,
    labId,
    queryObject,
  };

  // Only include fields actually used by the preview endpoint to avoid unnecessary refetches
  const previewCustomerResultsExportParams = {
    studyId,
    labId,
    queryObject,
  };

  const {
    isError: isManifestPreviewError,
    isFetched: hasFetchedManifestPreview,
    isFetching: isFetchingManifestPreview,
    data: previewResponse,
  } = usePreviewPrepareCustomerResultsExport(previewCustomerResultsExportParams);

  const hasCompletedExportPreview = hasFetchedManifestPreview && !isFetchingManifestPreview;
  const hasExportPreviewData = Array.isArray(previewResponse?.manifest);
  const hasExportPreviewResults = hasExportPreviewData && !isEmpty(previewResponse?.manifest);
  const showEmptyPreviewError = hasCompletedExportPreview && !isManifestPreviewError && !hasExportPreviewResults;

  const disablePrepareButton =
    prepareCustomerResultsMutation.isLoading ||
    isEmpty(resultTypesToExport) ||
    !studyId ||
    !hasValidUnitsPerZip ||
    !hasValidShardLimit ||
    isFetchingManifestPreview ||
    !hasCompletedExportPreview ||
    isManifestPreviewError ||
    !hasExportPreviewResults;

  const prepareButtonTooltip = isFetchingManifestPreview
    ? 'Waiting for the export preview to finish'
    : isManifestPreviewError
    ? 'The export preview failed.'
    : showEmptyPreviewError
    ? 'No files are expected for the current selection'
    : '';

  const exportPreviewStatus = isFetchingManifestPreview ? (
    <Alert severity="info" sx={{ mt: 2 }}>
      Checking which files will be exported for the current selection.
    </Alert>
  ) : isManifestPreviewError ? (
    <Alert severity="error" sx={{ mt: 2 }}>
      Unable to preview the export results for the current selection. Wait for the preview to succeed before starting
      the job.
    </Alert>
  ) : showEmptyPreviewError ? (
    <Alert severity="error" sx={{ mt: 2 }}>
      No exportable results are expected for the current selection. Adjust the filters or choose a different export
      scope to continue.
    </Alert>
  ) : null;

  const exportPreviewBody = isFetchingManifestPreview ? (
    <Typography variant="body1" color="textSecondary">
      <CircularProgress size={16} sx={{ mr: 1 }} />
      Loading list of exports...
    </Typography>
  ) : !isManifestPreviewError && hasExportPreviewData && hasExportPreviewResults ? (
    <PrepareCustomerResultsJobManifestTable
      previewMode
      manifest={previewResponse?.manifest}
      studyId={studyId}
      unpublishedExperimentResultIds={previewResponse?.unpublishedExperimentResultIds}
      queryStatistics={previewResponse?.queryStatistics}
      unitsPerZip={hasValidUnitsPerZip ? parsedUnitsPerZip : undefined}
      zipGranularity={zipGranularity}
    />
  ) : null;

  const studyExportBucketName = study?.settings?.exportBucket?.bucketName;

  return (
    <>
      <DialogContent dividers>
        {hasCaseParams ? (
          <Grid container direction="column" spacing={1} p={2}>
            <RadioGroup
              row
              value={filterByCaseParams ? 'filterByCaseParams' : 'exportAllPublishedResultsForStudy'}
              onChange={(event) => {
                setFilterByCaseParams(event.target.value === 'filterByCaseParams');
              }}
              sx={{ flexDirection: 'column' }}
            >
              <FormControlLabel
                value="exportAllPublishedResultsForStudy"
                control={<Radio />}
                label={`Export all published results for study ${study?.name || studyId}`}
              />
              {!filterByCaseParams && exportPreviewBody}
              <FormControlLabel
                value="filterByCaseParams"
                control={<Radio />}
                label={
                  hasSpecificSelections ? (
                    <>
                      Only export results for {caseParamsWithoutStudyId?.slidesMode ? 'slides' : 'cases'} matching the
                      following selection
                      {map(
                        manualSelectionFilters,
                        (filterName) =>
                          !isEmpty(caseParamsWithoutStudyId?.[filterName]) && (
                            <CohortQueryListItem
                              showIcon
                              key={filterName}
                              itemKey={filterName}
                              value={`${humanize(filterName)}: ${join(caseParamsWithoutStudyId?.[filterName], ', ')}`}
                            />
                          )
                      )}
                    </>
                  ) : (
                    <>
                      Export {lowerCase(caseParamsWithoutStudyId.resultsMode)} results
                      {isEmpty(caseParamsWithoutStudyId?.filters) && (
                        <> for the displayed {caseParamsWithoutStudyId?.slidesMode ? 'slides' : 'cases'}</>
                      )}
                      <> from study {study?.name || studyId}</>
                      {!isEmpty(caseParamsWithoutStudyId?.filters) && (
                        <>
                          {' '}
                          matching the following criteria
                          <CohortQueryList queryObject={caseParamsWithoutStudyId} showIcon />
                        </>
                      )}
                    </>
                  )
                }
              />
              {filterByCaseParams && exportPreviewBody}
            </RadioGroup>
            {exportPreviewStatus}
          </Grid>
        ) : (
          <Grid container direction="column" spacing={1} p={2}>
            <Typography variant="body2">Export all published results for study {study?.name || studyId}</Typography>
            {exportPreviewBody}
            {exportPreviewStatus}
          </Grid>
        )}
        <FilesToExportSelect selectedFilesToExport={resultTypesToExport} onChange={handleFilesToExportChange} />
        <Grid container direction="column" spacing={1} mt={2}>
          <Grid item>
            <FormControl>
              <FormLabel>Create a zip file</FormLabel>
              <RadioGroup
                row
                value={zipGranularity}
                onChange={(event) => setZipGranularity(event.target.value as 'slide' | 'case')}
              >
                <FormControlLabel value="slide" control={<Radio />} label="per slide" />
                <FormControlLabel value="case" control={<Radio />} label="per case" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item>
            <TextField
              label={`Number of ${zipGranularity}s per zip file`}
              type="number"
              value={unitsPerZipInput}
              onChange={(event) => setUnitsPerZipInput(event.target.value)}
              inputProps={{ min: 1, step: 1 }}
              error={!hasValidUnitsPerZip}
              helperText={!hasValidUnitsPerZip ? 'Enter a whole number greater than or equal to 1' : undefined}
              fullWidth
            />
          </Grid>
          <Grid item>
            <TextField
              label="Limit the number of parallel export jobs (defaults to the number of zip files)"
              type="number"
              value={shardLimitInput}
              onChange={(event) => setShardLimitInput(event.target.value)}
              inputProps={{ min: 1, step: 1 }}
              error={!hasValidShardLimit}
              helperText={
                !hasValidShardLimit ? 'Leave blank or enter a whole number greater than or equal to 1' : 'Optional'
              }
              fullWidth
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={generateFeaturesCsvFile}
                  onChange={() => setGenerateFeaturesCsvFile(!generateFeaturesCsvFile)}
                />
              }
              label="Generate features CSV file"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              disabled={!studyExportBucketName}
              control={
                <Checkbox
                  checked={publishResultsToCustomer}
                  onChange={() => setPublishResultsToCustomer(!publishResultsToCustomer)}
                  disabled={!studyExportBucketName}
                />
              }
              label={`Publish results to customer's bucket (${
                studyExportBucketName || 'bucket name not set in study settings'
              })`}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{cancelLabel}</Button>
        <Tooltip title={prepareButtonTooltip}>
          <span>
            <Button
              data-testid="prepare-customer-results-submit"
              variant="contained"
              onClick={async () => {
                try {
                  await prepareCustomerResultsMutation.mutateAsync(mutationParams);
                  onClose(undefined, true);
                } catch (error) {
                  console.error('Error preparing customer results:', error);
                }
              }}
              disabled={disablePrepareButton || prepareCustomerResultsMutation.isLoading}
            >
              {prepareCustomerResultsMutation.isLoading ? `Starting Job...` : 'Prepare'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </>
  );
};

export default PrepareCustomerResultsJob;
